import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api-client";
import { LoadingState } from "@/components/loading-state";
import { Panel } from "@/components/panel";
import { QueryErrorState } from "@/components/query-error-state";
import { asCurrency, formatRelativeTime } from "@/lib/format";

// Backend: GET /api/signals/whale-flow (signals router) — large-trade flow
// grouped by market and side. The previously queried
// /api/trader-intelligence/latest has NO backend route (the trader router is
// prefixed /api/traders and has no "latest"), so every fetch 404ed and the
// panel faked the calm "No intelligence data" state.
interface WhaleFlowEntry {
  market_id: string;
  side: string;
  total_value: number;
  trade_count: number;
  avg_price: number;
  min_price: number;
  max_price: number;
  latest_trade: string | null;
}

interface WhaleFlowResponse {
  timestamp: string;
  count: number;
  whale_trades: WhaleFlowEntry[];
  summary: Record<string, unknown>;
  latency_ms: number;
}

export function TraderIntelPanel() {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["trader-intel", "whale-flow"],
    queryFn: () => apiGet<WhaleFlowResponse>("/api/signals/whale-flow?limit=20"),
    staleTime: 60_000,
  });
  const entries = data?.whale_trades ?? [];
  return (
    <Panel title="Trader Intelligence">
      {isLoading ? (
        <LoadingState text="Loading trader intel…" />
      ) : isError ? (
        <QueryErrorState what="trader intelligence" onRetry={() => refetch()} />
      ) : entries.length === 0 ? (
        <p className="text-sm text-white/40 py-4 text-center">No whale flow detected</p>
      ) : (
        <div className="space-y-2">
          {entries.map((e, i) => (
            <div key={i} className="py-2 px-3 rounded-lg border border-white/5">
              <div className="flex items-start justify-between gap-2">
                <p className="text-xs text-white/80 truncate max-w-[200px]">{e.market_id}</p>
                <span className={`text-xs px-1.5 py-0.5 rounded font-semibold shrink-0 ${e.side === "BUY" ? "bg-green-500/20 text-green-400" : "bg-red-500/20 text-red-400"}`}>{e.side}</span>
              </div>
              <p className="text-xs text-white/40 mt-1">
                {e.trade_count} large trade{e.trade_count === 1 ? "" : "s"} · {asCurrency(e.total_value)} total
              </p>
              <div className="flex gap-3 mt-1 text-xs text-white/30">
                <span>avg price: {Number.isFinite(e.avg_price) ? e.avg_price.toFixed(2) : "—"}</span>
                <span>{e.latest_trade ? formatRelativeTime(e.latest_trade) : "—"}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}
